import React from 'react';
import { motion } from 'framer-motion';
import { BookImage, Zap } from 'lucide-react';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { LiveBadge } from '../UI/LiveBadge';
import styles from './AlbumTypeChoiceDialog.module.css';

interface AlbumTypeChoiceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onChoose: (type: 'normal' | 'live') => void;
}

const cards = [
  {
    type: 'normal' as const,
    icon: BookImage,
    title: 'Normal Album',
    description: 'Manually curate photos. Select which photos to include, configure overlays, and share with members.',
  },
  {
    type: 'live' as const,
    icon: Zap,
    title: 'Live Album',
    description: 'Photos are added automatically as they arrive from cameras or manual uploads. Overlays applied in real-time.',
    isLive: true,
  },
];

export function AlbumTypeChoiceDialog({ isOpen, onClose, onChoose }: AlbumTypeChoiceDialogProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" title="Create Album">
      <div className={styles['wrapper']}>
        <div className={styles['cards-grid']}>
          {cards.map(({ type, icon: Icon, title, description, isLive }) => (
            <motion.button
              key={type}
              onClick={() => onChoose(type)}
              whileHover={{ y: -2, scale: 1.01 }}
              whileTap={{ scale: 0.98 }}
              className={[
                styles['card'],
                type === 'live' ? styles['card-live'] : styles['card-normal'],
                styles['card-shadow'],
              ].join(' ')}
            >
              <div className={styles['card-content']}>
                <div className={type === 'live' ? styles['icon-wrap-live'] : styles['icon-wrap-normal']}>
                  <Icon size={20} strokeWidth={1.8} />
                </div>
                <div className={styles['card-body']}>
                  <div className={styles['card-title-row']}>
                    <span className={styles['card-title']}>
                      {title}
                    </span>
                    {isLive && <LiveBadge active={true} />}
                  </div>
                  <p className={styles['card-description']}>
                    {description}
                  </p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>

        <div className={styles['footer']}>
          <Button variant="ghost" size="sm" onClick={onClose} className={styles['btn-full']}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
